import { useEffect, useId, useRef, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import type { LogMode } from '../lib/prefs'

interface Props {
  mode: LogMode
  onChange: (mode: LogMode) => void
}

const OPTIONS: { value: LogMode; label: string }[] = [
  { value: 'hours', label: 'Log by number of hours' },
  { value: 'range', label: 'Log by start and end time' },
]

/**
 * The form's own settings, behind an ellipsis in its top corner.
 *
 * Out of the way on purpose: it is picked once and then never again, so it
 * should not sit in the flow of a form people fill in several times a day.
 *
 * Built by hand rather than with a library — it is two radio choices in a
 * popover, and the parts worth getting right are the ones a library would
 * not decide for us anyway: closing on Esc and on a click elsewhere,
 * returning focus to the button, and saying which option is current to a
 * screen reader as a choice rather than as two unrelated buttons.
 */
export function LogModeMenu({ mode, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent | TouchEvent) {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Back to the control that opened it, or focus is left on the body.
      buttonRef.current?.focus()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  function choose(next: LogMode) {
    onChange(next)
    setOpen(false)
    buttonRef.current?.focus()
  }

  return (
    <div className="menu" ref={boxRef}>
      <button
        type="button"
        ref={buttonRef}
        className="icon-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label="Time logging options"
        title="Time logging options"
        onClick={() => setOpen((was) => !was)}
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </button>

      {open && (
        <div className="menu-pop" id={menuId} role="menu" aria-label="How to enter a duration">
          {OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={mode === option.value}
              className={`menu-item${mode === option.value ? ' is-on' : ''}`}
              onClick={() => choose(option.value)}
            >
              {/* A tick, not just the weight: the current choice must not
                  rest on colour or boldness alone. */}
              <span className="menu-tick" aria-hidden="true">
                {mode === option.value ? '✓' : ''}
              </span>
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
